import { useMemo } from 'react';
import { useQuery } from 'react-query';
import isEmpty from 'lodash/isEmpty';
import HouseholdService from '../../services/householdService';

/**
 * Whether this household member can be deleted, and if not which of the three
 * blocks applies, via HouseholdService.getHouseholdMemberDeleteEligibility /
 * GET .../households/:householdId/members/:residentId/delete-eligibility.
 *
 * Head of household and open certification are also derivable client-side
 * (occupantInfo.isPrimary and useHouseholdOpenCertsQuery), but the third block
 * — the member already appearing on a completed certification — has no client
 * equivalent at all. Reading all three from the one endpoint the delete itself
 * shares its checks with is what keeps a disabled Delete button's reason from
 * drifting away from what the DELETE would actually have answered.
 *
 * residentId here is the MEMBER being deleted, not the resident whose profile
 * is open — the two differ for every member other than the one navigated to.
 *
 * Exactly one of residentId/applicantId names that member. A Pending member's
 * Resident row is only created at move-in, so it is read off the
 * applicant-scoped sibling route instead; the two ids come from different
 * tables, so the query key stays unambiguous with just the one that is set.
 */
const useHouseholdMemberDeleteEligibilityQuery = ({
  organizationId,
  propertyId,
  householdId,
  residentId,
  applicantId,
  options = {},
}: {
  organizationId: string,
  propertyId: string,
  householdId?: ?string,
  residentId?: string,
  applicantId?: string,
  options?: { enabled?: boolean },
}) => {
  const householdService = useMemo(() => new HouseholdService(), []);
  const memberId = applicantId ?? residentId;

  return useQuery(
    [
      'getHouseholdMemberDeleteEligibility',
      organizationId,
      propertyId,
      householdId,
      memberId,
    ],
    () =>
      applicantId
        ? householdService.getHouseholdApplicantDeleteEligibility(
            organizationId,
            propertyId,
            householdId,
            applicantId,
          )
        : householdService.getHouseholdMemberDeleteEligibility(
            organizationId,
            propertyId,
            householdId,
            residentId,
          ),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      enabled:
        !isEmpty(organizationId ?? '') &&
        !isEmpty(propertyId ?? '') &&
        !isEmpty(householdId ?? '') &&
        !isEmpty(memberId ?? '') &&
        options.enabled !== false,
    },
  );
};

export default useHouseholdMemberDeleteEligibilityQuery;
